import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { driver, DriveStep, Config } from 'driver.js';
import 'driver.js/dist/driver.css';
import '../styles/driver-custom.css';
import { useLocation } from 'react-router-dom';

interface TourContextType {
  startTour: () => void;
  stopTour: () => void;
  resetTour: () => void;
  isTourActive: boolean;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

interface TourProviderProps {
  children: ReactNode;
}

const TOUR_COMPLETED_KEY = 'roda-tour-completed';

const steps: DriveStep[] = [
  {
    popover: {
      title: '👋 Bem-vindo ao Sistema de Roda de Prêmios!',
      description: 'Vamos fazer um tour rápido pelas principais funcionalidades do sistema. Você pode pular este tour a qualquer momento clicando em "Pular" ou pressionando ESC.',
    },
  },
  {
    element: '[data-tour="dashboard"]',
    popover: {
      title: '📊 Dashboard',
      description: 'Aqui você visualiza as estatísticas principais do seu sistema, como total de rodadas, leads capturados e prêmios distribuídos.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="products"]',
    popover: {
      title: '🎁 Produtos/Prêmios',
      description: 'Gerencie os prêmios que serão distribuídos nas suas roletas. Configure nome, descrição, probabilidade e quantidade disponível.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="wheels"]',
    popover: {
      title: '🎰 Sorteios',
      description: 'Crie e personalize seus sorteios. Configure cores, textos, campos de captura e associe os prêmios que criou.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="leads"]',
    popover: {
      title: '👥 Clientes',
      description: 'Visualize todos os clientes capturados através das roletas, veja quais prêmios ganharam e exporte os dados para suas campanhas.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="reports"]',
    popover: {
      title: '📈 Relatórios',
      description: 'Acompanhe métricas detalhadas, gráficos de conversão e análise de performance das suas campanhas.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="subscription"]',
    popover: {
      title: '💳 Assinatura',
      description: 'Gerencie seu plano atual, veja limites de uso e faça upgrade quando precisar de mais recursos.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="user-menu"]',
    popover: {
      title: '👤 Menu do Usuário',
      description: 'Clique aqui para acessar seu perfil pessoal, alterar senha, acessar configurações ou sair do sistema.',
      side: 'top',
      align: 'end',
    },
  },
  {
    popover: {
      title: '🎉 Tour Completo!',
      description: 'Agora você conhece as principais funcionalidades do sistema. Você pode reiniciar este tour a qualquer momento clicando no botão "Tour de Introdução" no header.',
    },
  },
];

export const TourProvider: React.FC<TourProviderProps> = ({ children }) => {
  const [isTourActive, setIsTourActive] = useState(false);
  const driverRef = React.useRef<any>(null);
  const location = useLocation();


  useEffect(() => {
    const driverConfig: Config = {
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      steps,
      nextBtnText: 'Próximo',
      prevBtnText: 'Voltar',
      progressText: '{{current}} de {{total}}',
      onPopoverRender: (popover) => {
        const driverObj = driverRef.current;
        
        // Adicionar botão "Pular Tour" customizado
        const footer = popover.wrapper.querySelector('.driver-popover-footer');
        if (footer && !footer.querySelector('.skip-tour-btn')) {
          const skipBtn = document.createElement('button');
          skipBtn.className = 'skip-tour-btn';
          skipBtn.textContent = 'Pular Tour';
          skipBtn.style.cssText = `
            padding: 8px 16px;
            background-color: transparent;
            color: #ef4444;
            border: 1px solid #ef4444;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            margin-left: 8px;
            transition: all 0.2s;
          `;
          skipBtn.onmouseover = () => {
            skipBtn.style.backgroundColor = '#fef2f2';
          };
          skipBtn.onmouseout = () => {
            skipBtn.style.backgroundColor = 'transparent';
          };
          skipBtn.onclick = () => {
            if (driverObj) {
              driverObj.destroy();
              localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
            }
          };
          footer.appendChild(skipBtn);
        }

        // Mudar texto do último botão para "Finalizar"
        const nextBtn = popover.wrapper.querySelector('.driver-popover-next-btn');
        if (nextBtn && driverObj && !driverObj.hasNextStep()) {
          nextBtn.textContent = 'Finalizar';
        }
      },
      onDestroyStarted: () => {
        setIsTourActive(false);
        driverObj.destroy();
      },
      onDestroyed: () => {
        setIsTourActive(false);
        localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
      },
    };

    const driverObj = driver(driverConfig);
    driverRef.current = driverObj;

    // Verificar se o tour já foi completado e se estamos na página admin
    const tourCompleted = localStorage.getItem(TOUR_COMPLETED_KEY);
    const isInAdminPage = location.pathname.startsWith('/admin') &&
                          location.pathname !== '/' &&
                          location.pathname !== '/signup';

    if (!tourCompleted && isInAdminPage) {
      // Iniciar tour após 1.5 segundos para dar tempo da página carregar
      const timer = setTimeout(() => {
        setIsTourActive(true);
        driverObj.drive();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [
    location.pathname,
  ]);

  const startTour = () => {
    if (driverRef.current) {
      setIsTourActive(true);
      driverRef.current.drive();
    }
  };

  const stopTour = () => {
    if (driverRef.current) {
      setIsTourActive(false);
      driverRef.current.destroy();
    }
  };

  const resetTour = () => {
    localStorage.removeItem(TOUR_COMPLETED_KEY);
    if (driverRef.current) {
      setIsTourActive(true);
      driverRef.current.drive();
    }
  };

  return (
    <TourContext.Provider
      value={{
        startTour,
        stopTour,
        resetTour,
        isTourActive,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

export const useTour = (): TourContextType => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};
